<template>
  <div style="width: 100%; height: 100%">
    <canvas
      id="multiple-choice-chart"
      ref="barChart"
      @click="_showChartPercentage($event)"
      v-show="_isShowChart()"
      @mouseleave="resetColorChart"
    />
    <CanvasQuestionImage
      v-show="!_isShowChart()"
      @imageLoaded="isLoading = false"
      :isLoading="isLoading"
    />
  </div>
</template>
<script>
import Chart from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { setTimeout } from 'timers'
import { mapActions, mapGetters } from 'vuex'
import Constant from '@/constant'
import Util from '@/util'
import Socket from '@/socket'
import Config from '@/config'
import CanvasQuestionImage from '@/components-v2/presentation-editor/middle/CanvasQuestionImage.vue'

Chart.plugins.unregister(ChartDataLabels)

export default {
  components: {
    CanvasQuestionImage,
  },

  data() {
    const data = this._getData()
    const self = this
    return {
      activeIndexColumn: null,
      slideId: null,
      hideResult: false,
      text: '',
      isLoading: false,
      chartData: {
        type: 'bar',
        data: {
          labels: data.labels,
          datasets: [
            {
              label: '',
              backgroundColor: data.backgroundColor,
              hoverBackgroundColor: data.hoverBackgroundColor,
              borderColor: data.borderColor,
              hoverBorderColor: data.borderColor,
              borderWidth: 1,
              pointBackgroundColor: 'white',
              data: data.values,
            },
          ],
        },
        legend: {
          display: false,
        },
        options: {
          onHover(e) {
            const activePoints = self.myChart.getElementsAtEvent(e)
            if (self.$refs.barChart) {
              if (!activePoints.length) {
                self.$refs.barChart.style.cursor = 'default'
              } else {
                self.$refs.barChart.style.cursor = 'pointer'
                self.activeIndexColumn = activePoints[0]._index
              }
              self.updateDataColor()
            }
          },
          plugins: {
            datalabels: {
              align: 'end',
              anchor: 'end',
              offset: -2,
              color: self._getData().textColor,
              font: {
                size: self._getData().valueFontSize,
                family: self._getData().fontFamily,
              },
              formatter(value) {
                const allValues = self._getData().values
                const total = allValues.reduce((a, b) => a + b, 0)
                if (self.$store.state.slideActive.showPercentage) {
                  if (total == 0) {
                    return '0%'
                  }
                  if (((value / total) * 100) % 1 === 0) {
                    return `${(value / total) * 100}%`
                  }
                  return `${Number(`${Math.round(`${(value / total) * 100}e1`)}e-1`)}%`
                }
                return value
              },
            },
          },
          layout: {
            padding: data.padding,
          },
          tooltips: {
            enabled: true,
            mode: 'single',
            titleFontSize: 15,
            callbacks: {
              title(tooltipItem) {
                let tooltip = ''
                if (
                  self.$store.state.slideActive.SlideOptions[
                    tooltipItem[0].index
                  ]
                ) {
                  tooltip =
                    self.$store.state.slideActive.SlideOptions[
                      tooltipItem[0].index
                    ].title
                }

                let tooltipCheck = ''

                if (tooltip) {
                  tooltipCheck = tooltip.replace(/\s/g, '')
                }
                if (!tooltipCheck || tooltip === 'undefined') {
                  return '(Empty)'
                }
                return tooltip
              },
              label(tooltipItem) {
                const allValues = self._getData().values
                const value = self._getData().values[tooltipItem.index]
                const total = allValues.reduce((a, b) => a + b, 0)
                if (self.$store.state.slideActive.showPercentage) {
                  if (((value / total) * 100) % 1 === 0) {
                    return `${(value / total) * 100}%`
                  }
                  return `${Number(`${Math.round(`${(value / total) * 100}e1`)}e-1`)}%`
                }
                return value
              },
            },
          },
          scales: {
            yAxes: [
              {
                display: false,
                ticks: {
                  beginAtZero: true,
                  fontFamily: self._getData().fontFamily,
                  fontSize: data.valueFontSize,
                  max: data.limit,
                  fontColor: data.textColor,
                },
              },
            ],
            xAxes: [
              {
                gridLines: {
                  color: data.textColor,
                  lineWidth: 1,
                  display: true,
                  drawBorder: true,
                  drawOnChartArea: false,
                  drawTicks: false,
                },
                maxBarThickness: data.maxBarThickness,
                ticks: {
                  fontFamily: self._getData().fontFamily,
                  fontSize: data.labelFontSize,
                  fontColor: data.textColor,
                  beginAtZero: true,
                  autoSkip: false,
                  maxRotation: 0,
                  padding: 5,
                },
                barPercentage: 1,
              },
            ],
          },
          legend: {
            display: false,
          },

          maintainAspectRatio: false,
          responsive: true, // https://stackoverflow.com/a/52898198/4672162
          // events: [],
          animation: {
            duration: 200,
          },
        },
      },
      myChart: null,
    }
  },

  computed: {
    ...mapGetters(['isBackstage', 'isPresenting']),
  },

  watch: {
    '$store.state.slideActive': function () {
      if (this.$store.state.slideActive.type == 'pickAnswer' && this.isPresenting) {
        if (this.$store.state.slideActive.quizStatus == Constant.QuizStatuses.RESULT_SCREEN
          || this.$store.state.sharePresentation) {
          this.autoUpdateChart()
        }
        return
      }
      this.autoUpdateChart()
    },
    '$store.state.presentation.fontFamily': function () {
      this.updateData()
    },
  },

  mounted() {
    if (this.$store.state.sharePresentation) {
      this.updateSlideActiveAction({
        hideResult: false,
        showQuestionImage: false,
      })
    }
    this.slideId = this.$store.state.slideActive.id
    this.hideResult = this.$store.state.slideActive.hideResult
    this.createChart('multiple-choice-chart', this.chartData)
    if (this.$store.state.slideActive.type == 'pickAnswer') {
      this.updateSlideActiveAction({
        addCorrectOption: true,
      })

      if (!this.isPresenting && !this.isBackstage) {
        this.updateSlideActiveAction({
          showCorrectOption: false,
          hideResult: false,
        })
      } else {
        this.updateSlideActiveAction({
          showCorrectOption: false,
        })
      }

      if (this.$store.state.sharePresentation) {
        this.$store.state.tempAudienceCount = this.$store.state.slideActive.multipleCountAnswer
      }
    } else {
      this.$store.state.tempAudienceCount = this.$store.state.slideActive.multipleCountAnswer
    }
    // EventBus.$on("update-bar-chart", function () {
    //   self.autoUpdateChart();
    // })

    // this._getData();
  },
  // destroyed () {
  //   EventBus.$off("update-bar-chart");
  // },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    autoUpdateChart() {
      setTimeout(() => {
        if (this.$refs['slide-imgg'] && !this.$refs['slide-imgg'].complete) {
          this.isLoading = true
        }
      }, 1)
      if (this.slideId != this.$store.state.slideActive.id) {
        this.reRenderChart()
      } else {
        this.updateData(false)
      }
      this.slideId = this.$store.state.slideActive.id
    },
    setColors(colors) {
      if (!Number.isInteger(this.activeIndexColumn)) {
        return colors
      }
      const newColors = []
      colors.forEach((item, index) => {
        let addItem = item
        if (index !== this.activeIndexColumn) {
          // TH có alpha khác 1 rồi thì thôi ko đổi màu nữa
          const alpha = item.replace(/^.*,(.+)\)/, '$1')
          if (alpha == 1) {
            addItem = Util.changeRgbaColor(item, 0.5)
          }
        }
        newColors.push(addItem)
      })
      return newColors
    },
    _getData() {
      let labels = []
      let values = []
      let totalValues = 0
      let isCheckData = false
      const colors = JSON.parse(JSON.stringify(Constant.colors))
      const slide = this.$store.state.slideActive
      const options = slide.SlideOptions
      let limit = 0
      let borderColor = ''

      if (slide.type === 'pickAnswer' && this.$store.state.sharePresentation) {
        slide.showCorrectOption = true
      }

      options.forEach((item, key) => {
        if (slide.type === 'pickAnswer' && key > 7) {
          return
        }
        if (key > 29) return
        if (!item.count) item.count = 0
        if (item.id && item.title) {
          let { title } = item
          if (this.$store.state.sharePresentation || slide.type !== 'pickAnswer' || (slide.type === 'pickAnswer' && slide.quizStatus == Constant.QuizStatuses.RESULT_SCREEN && !this.$store.state.sharePresentation)) {
            if (
              slide.addCorrectOption &&
              slide.showCorrectOption &&
              item.correct
            ) {
              title = `✅ ${title}`
            }
            if (
              slide.addCorrectOption &&
              slide.showCorrectOption &&
              !item.correct
            ) {
              title = `❌ ${title}`
              colors[key] = Util.changeRgbaColor(colors[key])
            }
          }
          labels.push(title)
          values.push(item.count)
          totalValues += item.count
          isCheckData = true
        } else {
          // labels.push('')
          // values.push(0)
          isCheckData = true
        }
      })
      limit = Math.max(...values) + 1 // https://trello.com/c/XNrU6Kan

      if (!isCheckData) {
        labels = JSON.parse(JSON.stringify(Constant.chartDefaultData.labels))
        values = JSON.parse(JSON.stringify(Constant.chartDefaultData.values))
        limit = Constant.chartDefaultData.limit
      }

      if (slide.hideResult) {
        for (let i = 0; i < values.length; i++) {
          values[i] = 0
        }
      }

      const dataConfig = Util.getLabelChart(
        labels,
        this.$store.state.slideActive,
      )
      if (this.$store.state.slideActive.showPercentage && !this.$store.state.slideActive.hideResult) {
        if (labels.length >= 19 && labels.length < 25) {
          dataConfig.valueFontSize -= 2
        } else if (labels.length >= 25) {
          dataConfig.valueFontSize -= 6
        }
      }

      borderColor = '#ffffff'

      let fontFamily = "'Nunito', sans-serif"
      if (this.$store.state.presentation.fontFamily && this.$store.state.presentation.fontFamily != 'nunito') {
        const objectFont = Constant.fonts[this.$store.state.presentation.fontFamily]
        if (objectFont) {
          fontFamily = objectFont.value
        } else {
          fontFamily = this.$store.state.presentation.fontFamily
        }
      }
      return {
        labels: dataConfig.labels,
        values,
        totalValues,
        backgroundColor: colors,
        hoverBackgroundColor: colors,
        labelFontSize: dataConfig.labelFontSize,
        padding: dataConfig.padding,
        valueFontSize: dataConfig.valueFontSize,
        limit,
        maxBarThickness: dataConfig.maxBarThickness,
        textColor: dataConfig.fontColor,
        borderColor,
        fontFamily,
      }
    },

    createChart(chartId, chartData) {
      const ctx = document.getElementById(chartId)
      if (!ctx) return
      // let dataConfig = Util.getLabelChart(data.labels, this.$store.getters.isPresenting)
      // Save reference
      this.myChart = new Chart(ctx, {
        plugins: [ChartDataLabels],
        type: chartData.type,
        data: chartData.data,
        options: chartData.options,
      })
    },
    reRenderChart() {
      if (this.myChart) this.myChart.destroy()
      setTimeout(() => {
        this.createChart('multiple-choice-chart', this.chartData)
      }, 100)
    },
    resetColorChart() {
      this.activeIndexColumn = null
      this.updateDataColor()
    },
    updateDataColor() {
      if (!this.myChart) {
        return
      }
      const data = this._getData()
      this.myChart.data.datasets[0].backgroundColor = this.setColors(data.backgroundColor)
      if (document.getElementById('multiple-choice-chart')) {
        this.myChart.update()
      }
    },
    updateData() {
      const data = this._getData()
      if (!this.myChart) {
        setTimeout(() => {
          this.createChart('multiple-choice-chart', this.chartData)
        }, 1)
        return
      }
      if (this.myChart) {
        this.myChart.data.labels = data.labels
        this.myChart.data.datasets.forEach((dataset) => {
          dataset.data = data.values
        })
        this.myChart.data.datasets[0].backgroundColor = data.backgroundColor
        this.myChart.data.datasets[0].borderColor = data.borderColor
        this.myChart.options.scales.xAxes[0].ticks.minor.fontSize =
          data.labelFontSize
        this.myChart.options.scales.xAxes[0].ticks.minor.fontColor =
          data.textColor
        this.myChart.options.scales.xAxes[0].ticks.minor.fontFamily = data.fontFamily
        this.myChart.options.plugins.datalabels.color = data.textColor
        this.myChart.options.plugins.datalabels.font.size = data.valueFontSize
        this.myChart.options.plugins.datalabels.font.family = data.fontFamily
        // this.myChart.options.scales.yAxes[0].ticks.fontSize = data.valueFontSize;
        // this.myChart.options.scales.yAxes[0].ticks.fontColor = data.textColor;
        this.myChart.options.layout.padding = data.padding
        this.myChart.options.scales.yAxes[0].ticks.max = data.limit
        this.myChart.options.scales.yAxes[0].ticks.fontFamily = data.fontFamily
        this.myChart.options.scales.xAxes[0].gridLines.color = data.textColor
        this.myChart.options.scales.xAxes[0].maxBarThickness =
          data.maxBarThickness
        if (!document.getElementById('multiple-choice-chart')) {
          this.reRenderChart()
        }
        this.myChart.update()
      }
    },
    _showChartPercentage(e) {
      if (this.$store.state.slideActive.type == 'pickAnswer') {
        return
      }
      const activePoints = this.myChart.getElementsAtEvent(e)
      if (!activePoints.length) {
        // do nothing
      } else if (!this.$store.state.sharePresentation) {
        this.updateSlideActiveAction({
          showPercentage: !this.$store.state.slideActive.showPercentage,
        })
        this._updateSlide({
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          showPercentage: this.$store.state.slideActive.showPercentage,
        })
      }
    },
    _updateSlide(params) {
      if (
        this.$store.state.sharePresentation ||
        this.$store.state.slideActive.type == 'pickAnswer'
      ) {
        return
      }
      this.$store.dispatch('updateSlideActive', this.$store.state.slideActive)
      params.socketId = Socket.getSocketId()
      params.presenting = this.isPresenting
      this.$store.dispatch('setSyncPresentation', 1)
      Util.uploadSlide(
        this.$store.state.slideActive,
        this.$store.state.presentation,
      )

      this.$http
        .put(Config.api.updateSlide + this.$store.state.slideActive.id, params)
        .then(
          () => {
            Util.mixpanelTracking(
              'Slide',
              'Edit setting',
              'Presenter edit setting show percentage on chart',
            )
            this.$store.dispatch('setSyncPresentation', 2)
          },
          () => {
            this.$store.dispatch('setSyncPresentation', 3)
          },
        )
    },

    _isShowChart() {
      const presenting = this.$store.getters.isPresenting
      const { slideActive } = this.$store.state
      const noQuestionImg = !slideActive.showQuestionImage || !slideActive.image

      if ((!presenting && noQuestionImg) || (slideActive.type === 'pickAnswer' && presenting && !this.$store.state.sharePresentation) || (slideActive.type === 'multipleChoice' && presenting && noQuestionImg) || (slideActive.type === 'pickAnswer' && this.$store.state.sharePresentation && noQuestionImg)) {
        return true
      }
      return false
    },
  },
}
</script>
